import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, b2bApi, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { StatusPill, Card, fmtDate } from "@/components/common/Stat";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Lock, HeartHandshake, Stethoscope, Building2 } from "lucide-react";

function CategoryGrid({ categories, value, onChange, disabled, testPrefix }) {
  const t = useT();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
      {categories.map((c) => (
        <label key={c} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-cream-50 px-3 py-2 text-sm">
          <span className="text-ink-900">{t(`b2b.consent.cat.${c}`)}</span>
          <Switch checked={!!value[c]} disabled={disabled} onCheckedChange={(v) => onChange({ ...value, [c]: v })} data-testid={`${testPrefix}-cat-${c}`} />
        </label>
      ))}
    </div>
  );
}

/** One professional relationship (care or occupational health) with granular consent. */
function Relationship({ link, categories, who, onAccept, onDecline, onRevoke, testPrefix }) {
  const t = useT();
  const [cats, setCats] = useState(link.categories || {});
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const status = String(link.status || "pending").toLowerCase();
  const active = status === "active" || status === "accepted";
  const pending = status === "pending" || status === "invited" || status === "requested";
  return (
    <div className="rounded-2xl border border-border p-4" data-testid={`${testPrefix}-${link.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-ink-500">{pending ? t("b2b.consent.requestFrom") : who}</div>
          <div className="font-medium text-ink-900 truncate">{link.professional_name || link.professional_email || link.name || link.email || "—"}</div>
          {link.purpose && <div className="text-xs text-ink-500 mt-0.5">{t("b2b.oh.purpose")}: {link.purpose}</div>}
          {link.message && <div className="text-sm text-ink-700 mt-2 italic">“{link.message}”</div>}
          <div className="text-xs text-ink-500 mt-2">
            {active && link.accepted_at && `${t("b2b.consent.grantedOn")} ${fmtDate(link.accepted_at)}`}
            {status === "revoked" && link.revoked_at && `${t("b2b.consent.revokedOn")} ${fmtDate(link.revoked_at)}`}
          </div>
        </div>
        <StatusPill status={active ? "active" : status} label={active ? t("b2b.consent.activeLabel") : pending ? t("b2b.consent.pendingLabel") : status} />
      </div>
      {(pending || active) && (
        <>
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500 mt-4">{t("b2b.consent.categories")}</div>
          <CategoryGrid categories={categories} value={cats} onChange={setCats} disabled={busy} testPrefix={`${testPrefix}-${link.id}`} />
          <div className="flex flex-wrap gap-2 mt-4">
            <Button disabled={busy} onClick={() => run(() => onAccept(link, cats))} className="rounded-full bg-sage-500 hover:bg-sage-600 text-white h-10" data-testid={`${testPrefix}-${link.id}-accept`}>{t("b2b.consent.accept")}</Button>
            {pending && <Button disabled={busy} variant="outline" onClick={() => run(() => onDecline(link))} className="rounded-full h-10" data-testid={`${testPrefix}-${link.id}-decline`}>{t("b2b.consent.decline")}</Button>}
            {active && <Button disabled={busy} variant="outline" onClick={() => { if (window.confirm(t("b2b.consent.revokeConfirm"))) run(() => onRevoke(link)); }} className="rounded-full h-10 text-rust-600 border-rust-500/30" data-testid={`${testPrefix}-${link.id}-revoke`}>{t("b2b.consent.revoke")}</Button>}
          </div>
        </>
      )}
    </div>
  );
}

export default function ConsentCenter() {
  const t = useT();
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ["consent-org"], queryFn: async () => (await api.get("/consent")).data, retry: false });
  const care = useQuery({ queryKey: ["consent-care"], queryFn: async () => (await api.get("/care/dossier")).data, retry: false });
  const oh = useQuery({ queryKey: ["consent-oh"], queryFn: async () => (await b2bApi.get("/b2b/consent/center")).data, retry: false });

  const setMode = async (mode) => {
    try { await api.put("/consent", { ...(org.data?.consent || {}), mode }); toast.success(t("common.saved")); qc.invalidateQueries({ queryKey: ["consent-org"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const careLinks = Array.isArray(care.data?.links) ? care.data.links : [];
  const careCats = care.data?.consent_categories || [];
  const ohLinks = oh.data?.occupational_health || [];
  const ohCats = oh.data?.categories || [];
  const inv = (k) => qc.invalidateQueries({ queryKey: [k] });

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={t("nav.settings")} title={t("b2b.consent.title")} subtitle={t("b2b.consent.subtitle")} />
      <div className="rounded-3xl bg-sage-100/50 border border-sage-500/20 p-5 flex items-start gap-3" data-testid="consent-why">
        <Lock className="h-5 w-5 text-sage-700 mt-0.5 shrink-0" strokeWidth={1.5} />
        <div><div className="font-medium text-ink-900">{t("b2b.consent.whyTitle")}</div><p className="text-sm text-ink-700 mt-1">{t("b2b.consent.whyBody")}</p></div>
      </div>

      <Card title={<span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4" />{t("b2b.consent.orgSharing")}</span>} testId="consent-org-card">
        <p className="text-sm text-ink-500 mb-3">{t("b2b.consent.orgSharingBody")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {(org.data?.modes || []).map((m) => (
            <button key={m.id} type="button" onClick={() => setMode(m.id)} data-testid={`consent-mode-${m.id}`}
              className={`text-left rounded-2xl border p-4 transition-colors ${org.data?.consent?.mode === m.id ? "border-sage-500 bg-sage-100/50" : "border-border bg-white hover:bg-cream-50"}`}>
              <div className="font-medium text-ink-900 text-sm">{m.title}</div>
              <div className="text-xs text-ink-500 mt-1">{m.description}</div>
            </button>
          ))}
          {org.isLoading && <div className="text-sm text-ink-500">{t("common.loading")}</div>}
        </div>
      </Card>

      <Card title={<span className="inline-flex items-center gap-2"><HeartHandshake className="h-4 w-4" />{t("b2b.consent.care")}</span>} testId="consent-care-card">
        <div className="space-y-3">
          {careLinks.map((l) => (
            <Relationship key={l.id || l.link_id} link={{ ...l, id: l.id || l.link_id, categories: l.consent || l.categories || care.data?.default_consent || {} }} categories={careCats} who={t("b2b.consent.care")} testPrefix="care-link"
              onAccept={async (link, cats) => { if (["pending", "invited", "requested"].includes(String(link.status).toLowerCase())) await api.post(`/care/dossier/links/${link.id}/accept`); await api.post("/care/dossier/consent", { link_id: link.id, categories: cats }); toast.success(t("common.saved")); inv("consent-care"); }}
              onDecline={async (link) => { await api.post(`/care/dossier/links/${link.id}/decline`); inv("consent-care"); }}
              onRevoke={async (link) => { await api.post("/care/dossier/consent/revoke", { link_id: link.id }); inv("consent-care"); }} />
          ))}
          {!careLinks.length && <div className="text-sm text-ink-500">{care.isLoading ? t("common.loading") : t("b2b.consent.noRequests")}</div>}
        </div>
      </Card>

      <Card title={<span className="inline-flex items-center gap-2"><Stethoscope className="h-4 w-4" />{t("b2b.consent.oh")}</span>} testId="consent-oh-card">
        <div className="space-y-3">
          {ohLinks.map((l) => (
            <Relationship key={l.id} link={l} categories={ohCats} who={t("b2b.consent.oh")} testPrefix="oh-link"
              onAccept={async (link, cats) => { await b2bApi.post(`/b2b/consent/oh/${link.id}/accept`, { categories: cats }); toast.success(t("common.saved")); inv("consent-oh"); }}
              onDecline={async (link) => { await b2bApi.post(`/b2b/consent/oh/${link.id}/decline`); inv("consent-oh"); }}
              onRevoke={async (link) => { await b2bApi.post(`/b2b/consent/oh/${link.id}/revoke`); inv("consent-oh"); }} />
          ))}
          {!ohLinks.length && <div className="text-sm text-ink-500">{oh.isLoading ? t("common.loading") : t("b2b.consent.noRequests")}</div>}
        </div>
      </Card>
    </div>
  );
}
